import {
  Inbox,
  MessageSquareReply,
  Route,
  CalendarCheck,
  RefreshCw,
  BellRing,
  Megaphone,
  BarChart3,
  ArrowRight,
  ArrowDown,
  Check,
} from "lucide-react";
import { Reveal, Chapter } from "../components/Reveal";

const STEPS = [
  { icon: Inbox, title: "Patient Enquiry" },
  { icon: MessageSquareReply, title: "Immediate Response" },
  { icon: Route, title: "Qualification & Routing" },
  { icon: CalendarCheck, title: "Consultation Booking" },
  { icon: RefreshCw, title: "Follow-Up (if not booked)" },
  { icon: BellRing, title: "Appointment Reminder" },
  { icon: Megaphone, title: "Practice Notification" },
  { icon: BarChart3, title: "Results Tracking" },
];

const BENEFITS = [
  "Fast responses regardless of the time of day",
  "Consistent follow-up for people who don't book straight away",
  "Reactivation of older leads that never booked",
  "Fewer missed appointments through structured reminders",
  "Clear visibility into the flow of every enquiry",
  "A system that supports your staff rather than replacing them",
];

const StepCard = ({ step, index }) => (
  <div
    data-testid={`workflow-step-${index}`}
    className="flex items-center gap-4 rounded-xl border border-ink/10 bg-white px-5 py-4 lg:h-full lg:flex-col lg:items-start lg:gap-3 lg:px-4 lg:py-5"
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-soft">
      <step.icon size={18} className="text-teal" strokeWidth={1.8} />
    </div>
    <div>
      <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-teal">
        Step {index + 1}
      </span>
      <span className="mt-0.5 block text-sm font-medium leading-snug text-ink">
        {step.title}
      </span>
    </div>
  </div>
);

export default function Solution() {
  return (
    <section
      id="solution"
      data-testid="solution-section"
      className="scroll-mt-24 border-t border-ink/10 bg-sand/50 py-24 md:py-32"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="max-w-3xl">
          <Reveal>
            <Chapter number="02" label="The Solution" />
          </Reveal>
          <Reveal delay={60}>
            <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
              A structured way to handle every enquiry, consistently
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-6 text-base leading-relaxed text-ink/70 md:text-lg">
              Leadtochair sits alongside the channels you already use — your phone,
              website, WhatsApp and email — and puts a consistent structure
              around what happens after each enquiry arrives. It doesn&apos;t
              replace anything you have; it makes sure nothing quietly slips
              through.
            </p>
          </Reveal>
        </div>

        <div data-testid="workflow-diagram" className="mt-16">
          <div className="hidden lg:flex lg:items-stretch">
            {STEPS.map((step, i) => (
              <div key={step.title} className="flex min-w-0 flex-1 items-stretch">
                <Reveal delay={i * 140} className="flex-1">
                  <StepCard step={step} index={i} />
                </Reveal>
                {i < STEPS.length - 1 && (
                  <Reveal delay={i * 140 + 70} className="flex items-center px-1.5">
                    <ArrowRight size={16} className="shrink-0 text-teal" />
                  </Reveal>
                )}
              </div>
            ))}
          </div>

          <div className="relative lg:hidden">
            <div className="absolute bottom-6 left-[27px] top-6 w-px bg-teal-line" aria-hidden="true" />
            <div className="flex flex-col gap-5">
              {STEPS.map((step, i) => (
                <Reveal key={step.title} delay={i * 100}>
                  <div className="relative z-10">
                    <StepCard step={step} index={i} />
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="flex justify-start py-1 pl-[23px]" aria-hidden="true">
                      <ArrowDown size={14} className="text-teal" />
                    </div>
                  )}
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20">
          <Reveal>
            <h3 className="font-display text-2xl font-medium tracking-tight text-ink sm:text-3xl">
              What the practice gets
            </h3>
          </Reveal>
          <div className="mt-8 grid gap-x-12 gap-y-5 md:grid-cols-2">
            {BENEFITS.map((b, i) => (
              <Reveal key={b} delay={i * 80}>
                <div data-testid={`benefit-item-${i}`} className="flex items-start gap-3.5">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal">
                    <Check size={14} className="text-white" strokeWidth={2.5} />
                  </span>
                  <p className="text-sm leading-relaxed text-ink/75 md:text-base">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
